import React from 'react';
import { ProductionPKSData } from './types';
import { THEME_CONFIG } from './config';
import { formatNumber, formatPercentage } from './utils';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  TableContainer,
  useBreakpointValue,
} from '@chakra-ui/react';

interface ProduksiPKSProps {
  data: ProductionPKSData[];
}

const ProduksiPKS: React.FC<ProduksiPKSProps> = ({ data }) => {
  const getFormattedValue = (uraian: string, value: number) => {
    if (uraian.includes('%')) {
      return formatPercentage(value);
    }
    return formatNumber(value);
  };

  // Define common colors at the top level
  const bgCard = useColorModeValue('white', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'white');
  const tableHeadBg = useColorModeValue(THEME_CONFIG.COLORS.INFO, 'gray.600');
  const tableHeadText = useColorModeValue('white', 'white');
  const tableBorderColor = useColorModeValue('gray.200', 'gray.600');
  const tableRowBgOdd = useColorModeValue('white', 'gray.700');
  const tableRowBgEven = useColorModeValue('gray.50', 'gray.600');

  const radiusSize = useBreakpointValue({ 
      base: "4px",     // mobile - smaller
      md: "8px",       // tablet
      lg: "10px",      // laptop
      xl: "10px",      // desktop
      '2xl': "20px"    // large desktop
    }) || "10px";

    const marginBottom = useBreakpointValue({ 
      base: 2,         // mobile - smaller
      md: 2,           // tablet
      lg: 2,           // laptop
      xl: 2,           // desktop
      '2xl': 4         // large desktop
    }) || 2;

    const tableFontSize = useBreakpointValue({ 
      base: "xs",      // mobile - smaller
      md: "sm",        // tablet
      lg: "sm",        // laptop
      xl: "sm",        // desktop
      '2xl': "md"      // large desktop
    }) || "sm";

    const tableSize = useBreakpointValue({ 
      base: "xs",      // mobile - smaller
      md: "sm",        // tablet
      lg: "sm",        // laptop
      xl: "sm",        // desktop
      '2xl': "md"      // large desktop
    }) || "sm";

  return (
    <Box bg={bgCard} borderRadius="lg" p={4} h="full">
      <Heading as="h3" size="md" mb={marginBottom} color={textColor} fontSize="18px">
        PRODUKSI PKS
      </Heading>
      
      <TableContainer overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead>
            <Tr bgColor={tableHeadBg} height={radiusSize}>
              <Th color={tableHeadText} fontSize="sm" >Uraian</Th>
              <Th color={tableHeadText} isNumeric fontSize="sm">Harian</Th>
              <Th color={tableHeadText} isNumeric fontSize="sm">MTD</Th>
              <Th color={tableHeadText} isNumeric fontSize="sm">YTD</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.map((item, index) => (
              <Tr 
                key={`${item.uraian}-${item.date}`}
                bg={index % 2 === 0 ? tableRowBgEven : tableRowBgOdd}
              >
                <Td borderColor={tableBorderColor} fontWeight="medium" fontSize="sm">
                  {item.uraian}
                </Td>
                <Td borderColor={tableBorderColor} isNumeric fontFamily="mono" fontSize="sm">
                  {getFormattedValue(item.uraian, item.daily)}
                </Td>
                <Td borderColor={tableBorderColor} isNumeric fontFamily="mono" fontSize="sm">
                  {getFormattedValue(item.uraian, item.mtd)}
                </Td>
                <Td borderColor={tableBorderColor} isNumeric fontFamily="mono" fontSize="sm">
                  {getFormattedValue(item.uraian, item.ytd)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProduksiPKS;
